import { AppSearchInput } from "@/components/common/AppSearchInput";
import { useSelectClinicians } from "@/features/admin/api/selectClinicians";
import { supabaseClient } from "@/lib/supabase";
import { useNav } from "@/utils/nav";
import { Badge } from "@patient-watch/ui/badge";
import { Checkbox } from "@patient-watch/ui/checkbox";
import { toast } from "@patient-watch/ui/sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Building2 } from "lucide-react";
import { useMemo, useState } from "react";

export const Route = createFileRoute("/_app/customers/ambassadors")({
    component: AmbassadorsPage,
});

function AmbassadorsPage() {
    const { data, isLoading, error } = useSelectClinicians();
    const [search, setSearch] = useState("");
    const queryClient = useQueryClient();

    useNav([
        { label: "Customers", route: { to: "/customers/success" } },
        { label: "Ambassadors", route: { to: "/customers/ambassadors" } },
    ]);

    const updateAmbassador = useMutation({
        mutationFn: async ({ clinicianId, isAmbassador }: { clinicianId: string; isAmbassador: boolean }) => {
            const { error: updateError } = await supabaseClient
                .from("clinicians")
                .update({ is_ambassador: isAmbassador })
                .eq("clinician_id", clinicianId);
            if (updateError) throw updateError;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["admin-clinicians"] });
            await queryClient.invalidateQueries({ queryKey: ["admin-dashboard"] });
            toast.success("Ambassador status updated");
        },
        onError: () => {
            toast.error("Could not update ambassador status");
        },
    });

    const rows = useMemo(() => {
        const query = search.trim().toLowerCase();
        return (data ?? []).filter((clinician) => {
            const name = `${clinician.name ?? ""} ${clinician.email ?? ""}`.toLowerCase();
            if (query) return name.includes(query);
            return clinician.is_ambassador === true;
        });
    }, [data, search]);

    return (
        <div className="space-y-4">
            <div>
                <h2 className="font-semibold text-xl">Ambassadors</h2>
                <p className="text-muted-foreground text-sm">
                    Clinicians marked as Patient Watch ambassadors. Search to find someone and tick the box to add or
                    remove them.
                </p>
            </div>
            <AppSearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search clinicians by name or email"
                aria-label="Search clinicians"
                clearAriaLabel="Clear search"
                className="w-full sm:w-[280px]"
            />
            {isLoading ? (
                <p className="text-muted-foreground text-sm">Loading...</p>
            ) : error ? (
                <p className="text-destructive text-sm">Failed to load ambassadors</p>
            ) : (
                <div className="bg-white dark:bg-slate-900 shadow-sm border border-slate-200/80 dark:border-slate-700 rounded-md overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Organisations</TableHead>
                                <TableHead className="w-28">Ambassador</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((clinician) => {
                                const displayName = clinician.name || clinician.email || " - ";
                                return (
                                    <TableRow key={clinician.clinician_id}>
                                        <TableCell>
                                            <div className="font-medium">{displayName}</div>
                                            {clinician.email && clinician.name ? (
                                                <div className="text-muted-foreground text-sm">{clinician.email}</div>
                                            ) : null}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-wrap gap-1">
                                                {Array.isArray(clinician.organisations) &&
                                                clinician.organisations.length > 0 ? (
                                                    clinician.organisations.map((org) => (
                                                        <Badge
                                                            key={org.organisation_id}
                                                            variant="outline"
                                                            className="gap-1"
                                                        >
                                                            <Building2 className="w-3.5 h-3.5" />
                                                            {org.organisation_name}
                                                        </Badge>
                                                    ))
                                                ) : (
                                                    <span className="text-muted-foreground">No organisations</span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Checkbox
                                                checked={clinician.is_ambassador === true}
                                                disabled={updateAmbassador.isPending}
                                                aria-label={`Ambassador status for ${displayName}`}
                                                onCheckedChange={(checked) => {
                                                    updateAmbassador.mutate({
                                                        clinicianId: clinician.clinician_id,
                                                        isAmbassador: checked === true,
                                                    });
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    {rows.length === 0 ? (
                        <p className="px-4 py-6 text-muted-foreground text-sm">
                            {search.trim() ? "No clinicians match that search." : "No ambassadors yet."}
                        </p>
                    ) : null}
                </div>
            )}
        </div>
    );
}
